<template>
	<div class="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
		<div class="max-w-7xl mx-auto">
			<GlobalLoader :is-loading="isLoading" />
			<header class="mb-8">
				<h4 class="text-3xl font-bold text-gray-800">General Settings</h4>
			</header>

			<!-- Tabs Navigation -->
			<div class="bg-white rounded-lg shadow-sm mb-6">
				<div class="flex border-b border-gray-200 overflow-x-auto">
					<button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id" :class="[
						'flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-colors duration-150 border-b-2',
						activeTab === tab.id
							? 'text-blue-600 border-blue-600 bg-blue-50'
							: 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
					]">
						<q-icon :name="tab.icon" class="text-lg" />
						<span>{{ tab.label }}</span>
					</button>
				</div>
			</div>

			<!-- Tab Content -->
			<div class="space-y-8">
				<!-- General Tab -->
				<div v-show="activeTab === 'general'">

					<!-- Prize Pool Overview Section -->
					<section class="bg-white p-6 rounded-lg shadow-sm mt-6">
						<div class="flex justify-between items-center mb-6">
							<h4 class="text-xl font-semibold text-gray-700">Prize Pool Overview</h4>
							<button v-if="!isEditingPrizePool" type="button" @click="togglePrizePoolEditMode"
								class="flex items-center space-x-2 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition duration-150 font-medium shadow-md">
								<q-icon name="edit" />
								<span>Edit</span>
							</button>
						</div>

						<!-- Prize Pool Display View -->
						<div v-if="!isEditingPrizePool">
							<div class="text-center mb-6">
								<p class="text-2xl font-bold text-gray-800 !mb-1">€ {{
									formatNumber(prizePoolData.total_prize_amount || 0) }}</p>
								<p class="text-sm text-gray-500">Current Total Prize Pool</p>
							</div>

							<div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
								<div class="p-4 rounded-lg bg-green-50 flex items-center shadow-sm">
									<div class="flex-grow">
										<p class="text-xl font-bold text-green-700">€ {{
											formatNumber(gameConfig.entryFee || 0) }}</p>
										<p class="text-xs text-green-600 mt-1">Entry Fee per Gameweek</p>
										<p class="text-xs text-gray-500">Amount charged per entry</p>
									</div>
								</div>

								<div class="p-4 rounded-lg bg-violet-50 flex items-center shadow-sm">
									<div class="flex-grow">
										<p class="text-xl font-bold text-violet-700">€ {{
											formatNumber(prizePoolData.base_prize_amount || 0) }}</p>
										<p class="text-xs text-violet-600 mt-1">Base Prize Pool</p>
										<p class="text-xs text-gray-500">Fixed weekly amount</p>
									</div>
								</div>

								<div class="p-4 rounded-lg bg-orange-50 flex items-center shadow-sm">
									<div class="flex-grow">
										<p class="text-xl font-bold text-orange-700">€ {{
											formatNumber(prizePoolData.rollover_amount || 0) }}</p>
										<p class="text-xs text-orange-600 mt-1">Previous Week's Rollover</p>
										<p class="text-xs text-gray-500">from previous weeks</p>
									</div>
								</div>

								<div class="p-4 rounded-lg bg-blue-50 flex items-center shadow-sm">
									<div class="flex-grow">
										<p class="text-xl font-bold text-blue-700">€ {{
											formatNumber(prizePoolData.future_rollover_amount || 0) }}</p>
										<p class="text-xs text-blue-600 mt-1">Future Rollover Amount</p>
										<p class="text-xs text-gray-500">to be rolled over</p>
									</div>
								</div>
							</div>

							<div>
								<p class="text-sm font-medium text-gray-700 mb-2">Maximum Prize Pool (with rollover)</p>
								<div class="h-2 bg-gray-200 rounded-full relative">
									<div class="h-2 bg-purple-500 rounded-full"
										:style="{ width: `${prizePoolProgressPercent}%` }"></div>
								</div>
								<div class="flex justify-between text-xs mt-2 text-gray-500">
									<span class="font-semibold text-gray-800">€ {{
										formatNumber(prizePoolData.total_prize_amount || 0) }}</span>
									<span class="font-semibold text-gray-800">€ {{
										formatNumber(prizePoolData.maximum_prize_pool_withrollover || 0)
									}}</span>
								</div>
							</div>
						</div>

						<!-- Prize Pool Edit View -->
						<form v-else @submit.prevent="savePrizePoolSettings">
							<div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
								<div>
									<label class="text-gray-700 text-[15px] block mb-1">Entry Fee per Gameweek</label>
									<div class="relative flex items-stretch">
										<span
											class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg">
											€
										</span>
										<input type="number" v-model.number="gameConfig.entryFee"
											placeholder="Entry Fee Amount" min="0" step="0.01"
											class="flex-grow min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 shadow-sm" />
									</div>
									<p class="text-xs text-gray-500 mt-1">Amount charged to users for each gameweek
										entry</p>
									<p v-if="gameConfig.entryFee < 0" class="text-red-500 text-xs mt-1">Entry fee must
										be a non-negative number</p>
								</div>

								<div>
									<label class="text-gray-700 text-[15px] block mb-1">Base Prize Pool</label>
									<div class="relative flex items-stretch">
										<span
											class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg">
											€
										</span>
										<input type="number" v-model.number="prizePoolEdit.base_prize_amount"
											placeholder="Base Prize Pool Amount" min="100" max="99999"
											class="flex-grow min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 shadow-sm" />
									</div>
									<p v-if="prizePoolEdit.base_prize_amount != null && (Number(prizePoolEdit.base_prize_amount) < 100 || Number(prizePoolEdit.base_prize_amount) > 99999)"
										class="text-red-500 text-xs mt-1">
										Amount must be between 100 and 99999
									</p>
								</div>

								<div>
									<label class="text-gray-700 text-[15px] block mb-1">Future Rollover Amount</label>
									<div class="relative flex items-stretch">
										<span
											class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg">€</span>
										<input type="number" v-model.number="prizePoolEdit.future_rollover_amount"
											placeholder="Amount to rollover to next week" min="0"
											class="flex-grow min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 shadow-sm" />
									</div>
									<p class="text-xs text-gray-500 mt-1">Amount that will be rolled over to future
										weeks</p>
									<p v-if="prizePoolEdit.future_rollover_amount < 0"
										class="text-red-500 text-xs mt-1">Must be a
										non-negative number</p>
								</div>

								<div>
									<label class="text-gray-700 text-[15px] block mb-1">Maximum Prize Pool</label>
									<div class="relative flex items-stretch">
										<span
											class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg">€</span>
										<input type="number"
											v-model.number="prizePoolEdit.maximum_prize_pool_withrollover"
											placeholder="Maximum Prize Pool" min="100" max="99999"
											class="flex-grow min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 shadow-sm" />
									</div>
									<p v-if="prizePoolEdit.maximum_prize_pool_withrollover != null && (Number(prizePoolEdit.maximum_prize_pool_withrollover) < 100 || Number(prizePoolEdit.maximum_prize_pool_withrollover) > 99999)"
										class="text-red-500 text-xs mt-1">
										Amount must be between 100 and 99999
									</p>
									<p v-if="prizePoolEdit.maximum_prize_pool_withrollover != null && prizePoolEdit.base_prize_amount != null && Number(prizePoolEdit.maximum_prize_pool_withrollover) <= Number(prizePoolEdit.base_prize_amount)"
										class="text-red-500 text-xs mt-1">
										Maximum Prize Pool must be greater than Base Prize Pool
									</p>
								</div>
							</div>

							<div class="mb-4">
								<p class="text-sm font-medium text-gray-700 mb-2">Previous Week's Rollover (Read-only)
								</p>
								<div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
									<p class="text-lg font-semibold text-gray-800">€ {{
										formatNumber(prizePoolData.rollover_amount || 0) }}</p>
									<p class="text-xs text-gray-500 mt-1">Rollover amount from previous weeks</p>
								</div>
							</div>

							<div class="mb-4">
								<p class="text-sm font-medium text-gray-700 mb-2">Calculated Total Prize Pool</p>
								<div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
									<p class="text-lg font-semibold text-gray-800">€ {{
										formatNumber(calculatedTotalPrizePool) }}</p>
									<p class="text-xs text-gray-500 mt-1">Base Prize Pool + Previous Week's Rollover</p>
								</div>
							</div>

							<div v-if="prizePoolValidationError"
								class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
								<p class="text-red-600 text-sm">{{ prizePoolValidationError }}</p>
							</div>

							<div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
								<button type="button" @click="cancelPrizePoolEditMode"
									class="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-150 font-medium shadow-md">
									<q-icon name="close" />
									<span>Cancel</span>
								</button>
								<button type="submit" :disabled="!hasPrizePoolChanges || hasPrizePoolValidationErrors"
									class="flex items-center space-x-2 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition duration-150 font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600">
									<q-icon name="save" />
									<span>Save Changes</span>
								</button>
							</div>
						</form>
					</section>
				</div>

				<!-- Legal Documents Tab -->
				<div v-show="activeTab === 'legal'">
					<section class="bg-white p-6 rounded-lg shadow-sm">
						<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
							<h4 class="text-xl font-semibold text-gray-700">Legal Documents</h4>

							<!-- Upload button -->
							<!-- <button @click="openModal" :disabled="loading"
								class="mt-4 sm:mt-0 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed">
								<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
									stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round"
										d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
								</svg>
								{{ loading ? 'Uploading...' : 'Upload Document' }}
							</button> -->
						</div>
						<div class="overflow-x-auto">
							<table class="min-w-full divide-y divide-gray-200">
								<thead class="bg-gray-50">
									<tr>
										<th scope="col"
											class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Document Type</th>
										<th scope="col"
											class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Filename</th>
										<th scope="col"
											class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Upload Date</th>
										<th scope="col"
											class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Size</th>
										<th scope="col"
											class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status</th>
										<th scope="col"
											class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions</th>
									</tr>
								</thead>
								<tbody class="bg-white divide-y divide-gray-200">
									<tr v-for="doc in legalDocuments" :key="doc.id">
										<td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{
											doc.document_type }}</td>
										<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ doc.file_name
										}}
										</td>
										<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ doc.created_at
										}}
										</td>
										<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ doc.file_size
										}}
										</td>
										<td class="px-6 py-4 whitespace-nowrap">
											<span
												class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
												{{ doc.status }}
											</span>
										</td>
										<td class="py-4 flex gap-3 whitespace-nowrap text-sm font-medium space-x-4">
											<a :href="doc.file_path" download
												class="flex items-center text-gray-600 hover:text-white font-medium text-[15px] px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-500 transition-colors duration-200">
												<q-icon name="download" class="mr-1" />
												Download
											</a>
											<button @click="openModal(doc)"
												class="text-gray-600 hover:text-white font-medium !text-[15px] px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-500">
												<q-icon name="edit" class="mr-1" />Replace
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>
				</div>

				<!-- Payments Tab -->
				<div v-show="activeTab === 'payments'">
					<section class="bg-white p-6 rounded-lg shadow-sm">
						<DashboardCard title="Winners & Payouts" subtitle="Automatic winners and admin payout controls"
							:has-docs="true">
							<!-- Sub-tabs Navigation -->
							<div class="mb-4 border-b border-gray-200">
								<div class="flex space-x-1">
									<button @click="paymentSubTab = 'weekly'" :class="[
										'px-4 py-2 text-sm font-medium transition-colors duration-150 border-b-2',
										paymentSubTab === 'weekly'
											? 'text-blue-600 border-blue-600'
											: 'text-gray-600 border-transparent hover:text-gray-900'
									]">
										Weekly
									</button>
									<button @click="paymentSubTab = 'monthly'" :class="[
										'px-4 py-2 text-sm font-medium transition-colors duration-150 border-b-2',
										paymentSubTab === 'monthly'
											? 'text-blue-600 border-blue-600'
											: 'text-gray-600 border-transparent hover:text-gray-900'
									]">
										Monthly
									</button>
								</div>
							</div>

							<!-- Weekly Winners by Gameweek -->
							<div v-show="paymentSubTab === 'weekly'">
								<div v-if="winnersLoading" class="text-center py-8 text-gray-500">
									Loading winners...
								</div>
								<div v-else-if="weeklyWinnersByGameweek.length === 0"
									class="text-center py-8 text-gray-500 italic">
									No winners to display.
								</div>
								<div v-else class="space-y-4">
									<div v-for="(weekGroup, idx) in weeklyWinnersByGameweek" :key="weekGroup.gameweek"
										class="border border-gray-100 rounded-lg p-5 relative bg-gray-50">
										<!-- Gameweek Header -->
										<div class="flex justify-between items-center cursor-pointer"
											@click="toggleExpandedWeek(weekGroup.gameweek)">
											<div class="flex items-center justify-center gap-2">
												<q-icon
													:name="isExpandedWeek(weekGroup.gameweek) ? 'expand_less' : 'expand_more'"
													size="29px" class="text-gray-600 mb-4" />
												<p class="font-medium text-lg">
													Gameweek {{ weekGroup.gameweek }}
												</p>
											</div>
											<div class="text-sm text-gray-600">
												{{ weekGroup.winners.length }} winner{{ weekGroup.winners.length !== 1 ?
													's' : '' }}
											</div>
										</div>

										<!-- Expandable Content -->
										<transition name="fade">
											<div v-if="isExpandedWeek(weekGroup.gameweek)" class="mt-4">
												<q-table flat bordered dense :rows="weekGroup.winners"
													:columns="weeklyColumns" row-key="key" class="bg-white rounded-lg">
													<template #body-cell-username="props">
														<q-td :props="props">
															<span class="font-medium text-gray-800">{{
																props.row.username }}</span>
														</q-td>
													</template>

													<template #body-cell-totalPrizeWon="props">
														<q-td :props="props" class="text-center">
															€{{ (weeklyTotalPrizeByUsername[props.row.username] ||
																0).toFixed(2) }}
														</q-td>
													</template>

													<template #body-cell-points_scored="props">
														<q-td :props="props" class="text-center">
															{{ props.row.points_scored }}
														</q-td>
													</template>

													<template #body-cell-prize_amount="props">
														<q-td :props="props" class="text-center">
															€{{ Number(props.row.prize_amount || 0).toFixed(2) }}
														</q-td>
													</template>

													<template #body-cell-claim_status="props">
														<q-td :props="props">
															<span
																:class="props.row.claim_status === 'Claimed' ? 'text-green-600' : 'text-amber-600'">
																{{ props.row.claim_status || 'Pending' }}
															</span>
														</q-td>
													</template>

													<template #body-cell-payout_status="props">
														<q-td :props="props">
															<span
																:class="props.row.payout_status === 'completed' || props.row.payout_status === 'Completed' ? 'text-green-600' : props.row.payout_status === 'approved' || props.row.payout_status === 'Approved' ? 'text-blue-600' : 'text-rose-600'">
																{{ formatPayoutStatus(props.row.payout_status) }}
															</span>
														</q-td>
													</template>

													<template #body-cell-actions="props">
														<q-td :props="props">
															<div class="flex items-center gap-2">
																<template
																	v-if="!props.row.payout_done || (props.row.payout_status === 'pending' || !props.row.payout_status)">
																	<q-btn size="sm" color="primary" label="Approve"
																		@click="approvePayout(props.row)"
																		:disable="!props.row.payout_done" />
																	<q-btn size="sm" color="info" label="View"
																		@click="viewPayoutDetails(props.row)"
																		:disable="!props.row.payout_done" />
																</template>
																<template
																	v-else-if="props.row.payout_status === 'approved' || props.row.payout_status === 'Approved'">
																	<q-btn size="sm" color="positive"
																		label="Mark Completed"
																		@click="markCompleted(props.row)" />
																	<q-btn size="sm" color="info" label="View"
																		@click="viewPayoutDetails(props.row)" />
																</template>
																<template v-else>
																	<q-btn size="sm" color="info" label="View"
																		@click="viewPayoutDetails(props.row)" />
																</template>
															</div>
														</q-td>
													</template>
												</q-table>
											</div>
										</transition>
									</div>
								</div>
							</div>

							<!-- Monthly Winners by Month -->
							<div v-show="paymentSubTab === 'monthly'">
								<div v-if="winnersLoading" class="text-center py-8 text-gray-500">
									Loading winners...
								</div>
								<div v-else-if="monthlyWinnersByMonth.length === 0"
									class="text-center py-8 text-gray-500 italic">
									No winners to display.
								</div>
								<div v-else class="space-y-4">
									<div v-for="(monthGroup, idx) in monthlyWinnersByMonth" :key="monthGroup.month"
										class="border border-gray-100 rounded-lg p-5 relative bg-gray-50">
										<!-- Month Header -->
										<div class="flex justify-between items-center cursor-pointer"
											@click="toggleExpandedMonth(monthGroup.month)">
											<div class="flex items-center justify-center gap-2">
												<q-icon
													:name="isExpandedMonth(monthGroup.month) ? 'expand_less' : 'expand_more'"
													size="29px" class="text-gray-600 mb-4" />
												<p class="font-medium text-lg">
													{{ monthGroup.month }}
												</p>
											</div>
											<div class="text-sm text-gray-600">
												{{ monthGroup.winners.length }} winner{{ monthGroup.winners.length !== 1
													? 's' : '' }}
											</div>
										</div>

										<!-- Expandable Content -->
										<transition name="fade">
											<div v-if="isExpandedMonth(monthGroup.month)" class="mt-4">
												<q-table flat bordered dense :rows="monthGroup.winners"
													:columns="monthlyColumns" row-key="key" class="bg-white rounded-lg">
													<template #body-cell-username="props">
														<q-td :props="props">
															<span class="font-medium text-gray-800">{{
																props.row.username }}</span>
														</q-td>
													</template>

													<template #body-cell-totalPrizeWon="props">
														<q-td :props="props" class="text-center">
															€{{ (monthlyTotalPrizeByUsername[props.row.username] ||
																0).toFixed(2) }}
														</q-td>
													</template>

													<template #body-cell-points_scored="props">
														<q-td :props="props" class="text-center">
															{{ props.row.points_scored }}
														</q-td>
													</template>

													<template #body-cell-prize_amount="props">
														<q-td :props="props" class="text-center">
															€{{ Number(props.row.prize_amount || 0).toFixed(2) }}
														</q-td>
													</template>

													<template #body-cell-claim_status="props">
														<q-td :props="props">
															<span
																:class="props.row.claim_status === 'Claimed' ? 'text-green-600' : 'text-amber-600'">
																{{ props.row.claim_status || 'Pending' }}
															</span>
														</q-td>
													</template>

													<template #body-cell-payout_status="props">
														<q-td :props="props">
															<span
																:class="props.row.payout_status === 'completed' || props.row.payout_status === 'Completed' ? 'text-green-600' : props.row.payout_status === 'approved' || props.row.payout_status === 'Approved' ? 'text-blue-600' : 'text-rose-600'">
																{{ formatPayoutStatus(props.row.payout_status) }}
															</span>
														</q-td>
													</template>

													<template #body-cell-actions="props">
														<q-td :props="props">
															<div class="flex items-center gap-2">
																<template
																	v-if="!props.row.payout_done || (props.row.payout_status === 'pending' || !props.row.payout_status)">
																	<q-btn size="sm" color="primary" label="Approve"
																		@click="approvePayout(props.row)"
																		:disable="!props.row.payout_done" />
																	<q-btn size="sm" color="info" label="View"
																		@click="viewPayoutDetails(props.row)"
																		:disable="!props.row.payout_done" />
																</template>
																<template
																	v-else-if="props.row.payout_status === 'approved' || props.row.payout_status === 'Approved'">
																	<q-btn size="sm" color="positive"
																		label="Mark Completed"
																		@click="markCompleted(props.row)" />
																	<q-btn size="sm" color="info" label="View"
																		@click="viewPayoutDetails(props.row)" />
																</template>
																<template v-else>
																	<q-btn size="sm" color="info" label="View"
																		@click="viewPayoutDetails(props.row)" />
																</template>
															</div>
														</q-td>
													</template>
												</q-table>
											</div>
										</transition>
									</div>
								</div>
							</div>
						</DashboardCard>
					</section>
				</div>

				<!-- Activity Logs Tab -->
				<div v-show="activeTab === 'activity'">
					<section class="bg-white p-6 rounded-lg shadow-sm">
						<DashboardCard title="Activity Logs" subtitle="Admin actions and system activity history"
							:has-docs="false">
							<q-table flat bordered dense :rows="activityLogsData" :columns="activityLogsColumns"
								row-key="id" :loading="activityLogsLoading" :pagination="activityLogsPagination"
								no-data-label="No activity logs to display." loading-label="Loading activity logs..."
								rows-per-page-label="Rows per page" class="bg-white rounded-lg">
								<template #body-cell-admin_name="props">
									<q-td :props="props">
										<span class="font-medium text-gray-800">{{ props.row.admin_name }}</span>
										<br />
										<span class="text-xs text-gray-500">{{ props.row.admin_email }}</span>
									</q-td>
								</template>

								<template #body-cell-action_type="props">
									<q-td :props="props">
										<span class="px-2 py-1 text-xs font-medium rounded"
											:class="getActionTypeClass(props.row.action_type)">
											{{ formatActionType(props.row.action_type) }}
										</span>
									</q-td>
								</template>

								<template #body-cell-description="props">
									<q-td :props="props">
										<div class="max-w-md">
											<p class="text-sm text-gray-700">{{ props.row.description || 'N/A' }}</p>
											<div v-if="props.row.metadata && props.row.metadata.display"
												class="mt-2 text-xs text-gray-500">
												<div v-for="(value, key) in props.row.metadata.display" :key="key"
													class="mb-1">
													<template v-if="key === 'changes' && typeof value === 'object'">
														<div class="mt-1">
															<!-- <span class="font-medium">{{ formatMetadataKey(key) }}:</span> -->
															<div v-for="(changeItem, changeKey) in value"
																:key="changeKey">
																<div v-if="changeItem.field"
																	class="font-medium text-gray-600">
																	{{ changeItem.field }}
																</div>
																<div v-if="changeItem.before" class="mt-0.5">
																	<span class="text-gray-500">Before:</span>
																	<span class="ml-1">{{ changeItem.before }}</span>
																</div>
																<div v-if="changeItem.after" class="mt-0.5">
																	<span class="text-gray-500">After:</span>
																	<span class="ml-1">{{ changeItem.after }}</span>
																</div>
																<div v-if="changeItem.change" class="mt-0.5">
																	<span class="text-gray-500">Change:</span>
																	<span class="ml-1">
																		{{ formatChangeValue(changeItem.change) }}
																	</span>
																</div>
															</div>
														</div>
													</template>
													<template
														v-else-if="!(props.row.metadata.display.changes && (key === 'before' || key === 'after'))">
														<span class="font-medium">{{ formatMetadataKey(key) }}:</span>
														<span class="ml-1">{{ formatMetadataValue(value) }}</span>
													</template>
												</div>
											</div>
										</div>
									</q-td>
								</template>

								<template #body-cell-performed_at="props">
									<q-td :props="props">
										<span class="text-sm text-gray-600">
											{{ formatDateTime(props.row.performed_at) }}
										</span>
									</q-td>
								</template>
							</q-table>
						</DashboardCard>
					</section>
				</div>
			</div>
		</div>
	</div>

	<!-- Legal Documents Modal -->
	<div v-if="showModal"
		class="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.7)] bg-opacity-50 z-50">
		<div class="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
			<h4 class="text-lg font-semibold text-gray-800 mb-4">
				{{ isReplaceMode ? 'Replace Legal Document' : 'Upload Legal Document' }}
			</h4>

			<!-- Document Type -->
			<div class="mb-4">
				<label class="block text-sm font-medium text-gray-700 mb-1">
					Document Type
				</label>
				<input v-model="documentType" type="text" placeholder="e.g. Privacy Policy"
					class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500" />
			</div>

			<!-- File Upload -->
			<div class="mb-4">
				<label class="block text-sm font-medium text-gray-700 mb-1">
					Select File
				</label>
				<input ref="fileInput" type="file" accept=".pdf" @change="handleFileChange"
					class="block w-full text-sm text-gray-700 border rounded-md p-2 cursor-pointer focus:outline-none"
					:class="fileError ? 'border-red-500' : 'border-gray-300'" />
				<p v-if="selectedFile && !fileError" class="text-xs text-gray-500 mt-1">
					Selected: {{ selectedFile.name }}
				</p>
				<p v-if="fileError" class="text-xs text-red-600 mt-1">
					{{ fileError }}
				</p>
			</div>

			<!-- Buttons -->
			<div class="flex justify-end gap-3">
				<button @click="closeModal" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
					Cancel
				</button>
				<button @click="uploadDocument" :disabled="!documentType || !selectedFile || loading"
					class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed">
					{{ loading ? (isReplaceMode ? 'Replacing...' : 'Uploading...') : (isReplaceMode ? 'Replace' :
						'Upload') }}
				</button>
			</div>
		</div>
	</div>

	<!-- Legal Documents Success Modal -->
	<div v-if="showDocumentSuccess" :class="{
		'fixed inset-0 w-full h-full flex items-center justify-center z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-95 overflow-hidden': !$q.screen.lt.sm,
		'fixed inset-0 w-full h-full flex flex-col z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-60 overflow-hidden': $q.screen.lt.sm
	}">
		<q-card
			:class="{ 'px-10 md:px-28 py-16 rounded-xl flex flex-col gap-8 min-w-[50vw]': !$q.screen.lt.sm, 'px-10 md:px-28 py-10 flex flex-col gap-8 min-w-[50vw] h-full justify-between': $q.screen.lt.sm }">
			<div class="flex items-center justify-between">
				<p class="text-2xl text-[#4F39F6] font-extrabold">
					LEGAL DOCUMENTS
				</p>
				<q-btn flat round dense icon="close" color="black" @click="closeDocumentSuccess" />
			</div>
			<div class="flex flex-col items-center text-center">
				<div class="flex flex-col items-center justify-center mb-2">
					<SuccessSvg class="w-10 h-10" />
					<p class="text-[20px] md:text-[26px] lg:text-[28px] font-bold text-[#4F39F6] my-2">
						Document {{ documentActionType === 'uploaded' ? 'Upload' : 'Replace' }}
					</p>
				</div>
				<p class="text-[#666666] text-xl text-center my-3 max-w-lg">
					Your document has been {{ documentActionType }} successfully
				</p>
			</div>
			<div>
				<q-btn label="Okay" type="submit" @click="closeDocumentSuccess"
					class="full-width rounded-xl text-white text-xl font-extrabold"
					style=" background-color: #4F39F6; font-style: italic" />
			</div>
		</q-card>
	</div>

	<!-- Settings Success Modal -->
	<div v-if="showSettingsSuccess" :class="{
		'fixed inset-0 w-full h-full flex items-center justify-center z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-95 overflow-hidden': !$q.screen.lt.sm,
		'fixed inset-0 w-full h-full flex flex-col z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-60 overflow-hidden': $q.screen.lt.sm
	}">
		<q-card
			:class="{ 'px-10 md:px-28 py-16 rounded-xl flex flex-col gap-8 min-w-[50vw]': !$q.screen.lt.sm, 'px-10 md:px-28 py-10 flex flex-col gap-8 min-w-[50vw] h-full justify-between': $q.screen.lt.sm }">
			<div class="flex items-center justify-between">
				<p class="text-2xl text-[#4F39F6] font-extrabold">
					SETTINGS
				</p>
				<q-btn flat round dense icon="close" color="black" @click="closeSettingsSuccess" />
			</div>
			<div class="flex flex-col items-center text-center">
				<div class="flex flex-col items-center justify-center mb-2">
					<SuccessSvg class="w-10 h-10" />
					<p class="text-[20px] md:text-[26px] lg:text-[28px] font-bold text-[#4F39F6] my-2">
						Settings Update
					</p>
				</div>
				<p class="text-[#666666] text-xl text-center my-3 max-w-lg">
					Your settings have been saved successfully
				</p>
			</div>
			<div>
				<q-btn label="Okay" type="submit" @click="closeSettingsSuccess"
					class="full-width rounded-xl text-white text-xl font-extrabold"
					style=" background-color: #4F39F6; font-style: italic" />
			</div>
		</q-card>
	</div>

	<!-- Payout Details Modal -->
	<div v-if="showPayoutDetailsModal"
		class="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.7)] bg-opacity-50 z-50">
		<div class="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto p-6 m-4">
			<div class="flex items-center justify-between mb-4">
				<h4 class="text-xl font-semibold text-gray-800">Payout Details</h4>
				<q-btn flat round dense icon="close" color="black" @click="closePayoutDetailsModal" />
			</div>

			<div v-if="payoutDetailsLoading" class="text-center py-8 text-gray-500">
				Loading payout details...
			</div>

			<div v-else-if="!payoutDetails" class="text-center py-8 text-gray-500">
				No payout details available. The payout may not have been claimed yet.
			</div>

			<div v-else class="space-y-6">
				<!-- User Information -->
				<div class="border-b border-gray-200 pb-4">
					<h5 class="text-lg font-semibold text-gray-700 mb-3">User Information</h5>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label class="text-sm font-medium text-gray-500">Username</label>
							<p class="text-base text-gray-900">{{ payoutDetails.username || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Email</label>
							<p class="text-base text-gray-900">{{ payoutDetails.email || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Full Name</label>
							<p class="text-base text-gray-900">{{ payoutDetails.full_name || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Date of Birth</label>
							<p class="text-base text-gray-900">{{ payoutDetails.date_of_birth || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Phone Number</label>
							<p class="text-base text-gray-900">{{ payoutDetails.phone_number || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Postal Address</label>
							<p class="text-base text-gray-900">{{ payoutDetails.postal_address || 'N/A' }}</p>
						</div>
					</div>
				</div>

				<!-- Payout Information -->
				<div class="border-b border-gray-200 pb-4">
					<h5 class="text-lg font-semibold text-gray-700 mb-3">Payout Information</h5>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label class="text-sm font-medium text-gray-500">Payout Type</label>
							<p class="text-base text-gray-900">{{ payoutDetails.payout_type || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Payout Period</label>
							<p class="text-base text-gray-900">{{ payoutDetails.payout_period || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Payout Status</label>
							<p class="text-base" :class="{
								'text-green-600': payoutDetails.payout_status === 'completed' || payoutDetails.payout_status === 'Completed',
								'text-blue-600': payoutDetails.payout_status === 'approved' || payoutDetails.payout_status === 'Approved',
								'text-rose-600': !payoutDetails.payout_status || payoutDetails.payout_status === 'pending'
							}">
								{{ formatPayoutStatus(payoutDetails.payout_status) }}
							</p>
						</div>
					</div>
				</div>

				<!-- Bank Account Information -->
				<div class="border-b border-gray-200 pb-4">
					<h5 class="text-lg font-semibold text-gray-700 mb-3">Bank Account Information</h5>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label class="text-sm font-medium text-gray-500">Account Holder Name</label>
							<p class="text-base text-gray-900">{{ payoutDetails.account_holder_name || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Bank Name</label>
							<p class="text-base text-gray-900">{{ payoutDetails.bank_name || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Sort Code</label>
							<p class="text-base text-gray-900">{{ payoutDetails.sort_code || 'N/A' }}</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Account Number</label>
							<p class="text-base text-gray-900">{{ payoutDetails.account_number ?
								payoutDetails.account_number : 'N/A' }}</p>
						</div>
					</div>
				</div>

				<!-- Documents -->
				<div class="border-b border-gray-200 pb-4">
					<h5 class="text-lg font-semibold text-gray-700 mb-3">Documents</h5>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div v-if="payoutDetails.photo_id_name">
							<label class="text-sm font-medium text-gray-500">Photo ID</label>
							<p class="text-base text-gray-900">{{ payoutDetails.photo_id_name }}</p>
							<a v-if="payoutDetails.photo_id_path" :href="payoutDetails.photo_id_path" target="_blank"
								class="text-blue-600 hover:text-blue-800 text-sm underline">View Document</a>
						</div>
						<div v-if="payoutDetails.proof_of_address_name">
							<label class="text-sm font-medium text-gray-500">Proof of Address</label>
							<p class="text-base text-gray-900">{{ payoutDetails.proof_of_address_name }}</p>
							<a v-if="payoutDetails.proof_of_address_path" :href="payoutDetails.proof_of_address_path"
								target="_blank" class="text-blue-600 hover:text-blue-800 text-sm underline">View
								Document</a>
						</div>
					</div>
				</div>

				<!-- Consent Information -->
				<div>
					<h5 class="text-lg font-semibold text-gray-700 mb-3">Consent</h5>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label class="text-sm font-medium text-gray-500">Declaration Consent</label>
							<p class="text-base text-gray-900">{{ payoutDetails.declaration_consent ? 'Yes' : 'No' }}
							</p>
						</div>
						<div>
							<label class="text-sm font-medium text-gray-500">Publicity Consent</label>
							<p class="text-base text-gray-900">{{ payoutDetails.publicity_consent ? 'Yes' : 'No' }}</p>
						</div>
					</div>
				</div>

				<!-- Timestamps -->
				<div v-if="payoutDetails.created_at || payoutDetails.updated_at" class="pt-4 border-t border-gray-200">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div v-if="payoutDetails.created_at">
							<label class="text-sm font-medium text-gray-500">Created At</label>
							<p class="text-base text-gray-900">{{ formatDateTime(payoutDetails.created_at) }}</p>
						</div>
						<div v-if="payoutDetails.updated_at">
							<label class="text-sm font-medium text-gray-500">Updated At</label>
							<p class="text-base text-gray-900">{{ formatDateTime(payoutDetails.updated_at) }}</p>
						</div>
					</div>
				</div>
			</div>

			<div class="mt-6 flex justify-end">
				<q-btn label="Close" color="primary" @click="closePayoutDetailsModal" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { onMounted, computed } from 'vue';
import { ref } from 'vue';
import GlobalLoader from '../components/GlobalLoader.vue';
import DashboardCard from '../components/DashboardCard.vue';
import SuccessSvg from '../components/svg/SuccessSvg.vue';

const activeTab = ref('general');
const paymentSubTab = ref('weekly');

const tabs = [
	{ id: 'general', label: 'General', icon: 'settings' },
	{ id: 'payments', label: 'Payments (Payout)', icon: 'euro' },
	{ id: 'legal', label: 'Legal Documents', icon: 'description' },
	{ id: 'activity', label: 'Activity Logs', icon: 'history' },
];

const gameConfig = ref({
	entryFee: 0,
	maxRolloverAmount: 0,
	prizePoolType: null,
	fixedPrizeAmount: 0,
	rolloverAmount: 0,
});
const originalGameConfig = ref({
	entryFee: 0,
	maxRolloverAmount: 0,
	prizePoolType: null,
	fixedPrizeAmount: 0,
	rolloverAmount: 0,
});
const legalDocuments = ref([]);

// Prize Pool state
const prizePoolData = ref({
	total_prize_amount: 0,
	base_prize_amount: 0,
	rollover_amount: 0,
	maximum_prize_pool_withrollover: 0,
	future_rollover_amount: 0,
});
const originalPrizePoolData = ref({
	base_prize_amount: 0,
	future_rollover_amount: 0,
	maximum_prize_pool_withrollover: 0,
});
const prizePoolEdit = ref({
	base_prize_amount: 0,
	future_rollover_amount: 0,
	maximum_prize_pool_withrollover: 0,
});
const isEditingPrizePool = ref(false);
const prizePoolValidationError = ref('');

const showModal = ref(false)
const documentType = ref('')
const selectedFile = ref(null)
const loading = ref(false)
const showSuccess = ref(false)
const isReplaceMode = ref(false)
const selectedDocId = ref(null)
const isLoading = ref(false);
const showSettingsSuccess = ref(false);
const showDocumentSuccess = ref(false);
const documentActionType = ref('uploaded');
const fileError = ref('');
const fileInput = ref(null);

// Winners & Payouts state
const winnersLoading = ref(false);
const weeklyWinnersData = ref([]);
const monthlyWinnersData = ref([]);
const winnersPagination = ref({ rowsPerPage: 10, page: 1 });
const expandedWeeks = ref({});
const expandedMonths = ref({});

// Payout details modal state
const showPayoutDetailsModal = ref(false);
const payoutDetails = ref(null);
const payoutDetailsLoading = ref(false);

// Activity Logs state
const activityLogsLoading = ref(false);
const activityLogsData = ref([]);
const activityLogsPagination = ref({ rowsPerPage: 10, page: 1 });

// Weekly columns (with Gameweek)
const weeklyColumns = [
	{ name: 'username', label: 'Username', field: 'username', align: 'left', sortable: true },
	{ name: 'totalPrizeWon', label: 'Total Prize Won', field: 'totalPrizeWon', align: 'center', sortable: false },
	{ name: 'points_scored', label: 'Points Scored', field: 'points_scored', align: 'center', sortable: true },
	{ name: 'prize_amount', label: 'Prize Amount', field: 'prize_amount', align: 'center', sortable: true },
	{ name: 'claim_status', label: 'Claim Status', field: 'claim_status', align: 'left', sortable: true },
	{ name: 'payout_status', label: 'Payout Status', field: 'payout_status', align: 'left', sortable: true },
	{ name: 'actions', label: 'Actions', field: 'actions', align: 'left' },
];

// Monthly columns (with Month instead of Gameweek)
const monthlyColumns = [
	{ name: 'username', label: 'Username', field: 'username', align: 'left', sortable: true },
	{ name: 'totalPrizeWon', label: 'Total Prize Won', field: 'totalPrizeWon', align: 'center', sortable: false },
	{ name: 'points_scored', label: 'Points Scored', field: 'points_scored', align: 'center', sortable: true },
	{ name: 'prize_amount', label: 'Prize Amount', field: 'prize_amount', align: 'center', sortable: true },
	{ name: 'claim_status', label: 'Claim Status', field: 'claim_status', align: 'left', sortable: true },
	{ name: 'payout_status', label: 'Payout Status', field: 'payout_status', align: 'left', sortable: true },
	{ name: 'actions', label: 'Actions', field: 'actions', align: 'left' },
];

// Activity Logs columns
const activityLogsColumns = [
	{ name: 'admin_name', label: 'Admin', field: 'admin_name', align: 'left', sortable: true },
	{ name: 'action_type', label: 'Action Type', field: 'action_type', align: 'left', sortable: true },
	{ name: 'description', label: 'Description', field: 'description', align: 'left', sortable: false },
	{ name: 'performed_at', label: 'Date & Time', field: 'performed_at', align: 'left', sortable: true },
];

const weeklyTotalPrizeByUsername = computed(() => {
	const acc = {};
	weeklyWinnersData.value.forEach((row) => {
		const key = row.username;
		const amount = Number(row.prize_amount || 0);
		acc[key] = (acc[key] || 0) + amount;
	});
	return acc;
});

const monthlyTotalPrizeByUsername = computed(() => {
	const acc = {};
	monthlyWinnersData.value.forEach((row) => {
		const key = row.username;
		const amount = Number(row.prize_amount || 0);
		acc[key] = (acc[key] || 0) + amount;
	});
	return acc;
});

// Prize Pool computed properties
const prizePoolProgressPercent = computed(() => {
	if (!prizePoolData.value.maximum_prize_pool_withrollover || prizePoolData.value.maximum_prize_pool_withrollover === 0) return 0;
	const total = prizePoolData.value.total_prize_amount || 0;
	const max = prizePoolData.value.maximum_prize_pool_withrollover || 0;
	return Math.min(100, (total / max) * 100);
});

const calculatedTotalPrizePool = computed(() => {
	const base = Number(prizePoolEdit.value.base_prize_amount) || 0;
	// Use previous week's rollover (from analytics) for current total calculation
	const rollover = Number(prizePoolData.value.rollover_amount) || 0;
	return base + rollover;
});

const hasPrizePoolChanges = computed(() => {
	if (!isEditingPrizePool.value) {
		return false;
	}

	const hasChanged = (current, original) => {
		const currentVal = Number(current) || 0;
		const originalVal = Number(original) || 0;
		return currentVal !== originalVal;
	};

	return (
		hasChanged(gameConfig.value.entryFee, originalGameConfig.value.entryFee) ||
		hasChanged(prizePoolEdit.value.base_prize_amount, originalPrizePoolData.value.base_prize_amount) ||
		hasChanged(prizePoolEdit.value.future_rollover_amount, originalPrizePoolData.value.future_rollover_amount) ||
		hasChanged(prizePoolEdit.value.maximum_prize_pool_withrollover, originalPrizePoolData.value.maximum_prize_pool_withrollover)
	);
});

const hasPrizePoolValidationErrors = computed(() => {
	if (!isEditingPrizePool.value) {
		return false;
	}

	if (prizePoolValidationError.value && prizePoolValidationError.value.trim() !== '') {
		return true;
	}

	// Validate entry fee
	if (gameConfig.value.entryFee < 0) {
		return true;
	}

	if (prizePoolEdit.value.base_prize_amount != null) {
		const baseNum = Number(prizePoolEdit.value.base_prize_amount);
		if (baseNum < 100 || baseNum > 99999) {
			return true;
		}
	}

	if (prizePoolEdit.value.future_rollover_amount < 0) {
		return true;
	}

	if (prizePoolEdit.value.maximum_prize_pool_withrollover != null) {
		const maxNum = Number(prizePoolEdit.value.maximum_prize_pool_withrollover);
		if (maxNum < 100 || maxNum > 99999) {
			return true;
		}
	}

	if (prizePoolEdit.value.maximum_prize_pool_withrollover != null && prizePoolEdit.value.base_prize_amount != null) {
		if (Number(prizePoolEdit.value.maximum_prize_pool_withrollover) <= Number(prizePoolEdit.value.base_prize_amount)) {
			return true;
		}
	}

	return false;
});

// Group weekly winners by gameweek
const weeklyWinnersByGameweek = computed(() => {
	const grouped = {};
	weeklyWinnersData.value.forEach((winner) => {
		const gameweek = winner.gameweek || 'Unknown';
		if (!grouped[gameweek]) {
			grouped[gameweek] = [];
		}
		grouped[gameweek].push(winner);
	});
	// Convert to array and sort by gameweek number
	return Object.keys(grouped)
		.sort((a, b) => {
			const numA = parseInt(a.toString().replace(/\D/g, '')) || 0;
			const numB = parseInt(b.toString().replace(/\D/g, '')) || 0;
			return numB - numA; // Descending order (newest first)
		})
		.map((gameweek) => ({
			gameweek,
			winners: grouped[gameweek],
		}));
});

// Group monthly winners by month
const monthlyWinnersByMonth = computed(() => {
	const grouped = {};
	monthlyWinnersData.value.forEach((winner) => {
		const month = winner.month || 'Unknown';
		if (!grouped[month]) {
			grouped[month] = [];
		}
		grouped[month].push(winner);
	});
	// Convert to array and sort by month
	return Object.keys(grouped)
		.sort((a, b) => {
			// Try to parse dates for proper sorting
			const dateA = new Date(a);
			const dateB = new Date(b);
			if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
				return dateB - dateA; // Descending order (newest first)
			}
			return b.localeCompare(a); // String comparison as fallback
		})
		.map((month) => ({
			month,
			winners: grouped[month],
		}));
});

const toggleExpandedWeek = (gameweek) => {
	expandedWeeks.value[gameweek] = !expandedWeeks.value[gameweek];
};

const isExpandedWeek = (gameweek) => {
	return expandedWeeks.value[gameweek] || false;
};

const toggleExpandedMonth = (month) => {
	expandedMonths.value[month] = !expandedMonths.value[month];
};

const isExpandedMonth = (month) => {
	return expandedMonths.value[month] || false;
};

onMounted(() => {
	GetDetails();
	fetchPrizePoolData();
	winnersLoading.value = true;
	Promise.all([fetchWeeklyWinners(), fetchMonthlyWinners()]).finally(() => {
		winnersLoading.value = false;
	});
	fetchActivityLogs();
	// GetLegalDocumentsDetails();
})

const GetDetails = async () => {
	isLoading.value = true;
	try {
		const [platform_setting, legal_documents] = await Promise.all([
			fetch('/platform_setting'),
			fetch('/legal_documents')
		])

		const [val, legal_Documents] = await Promise.all([platform_setting.json(), legal_documents.json()]);
		if (val) {
			gameConfig.value.entryFee = val.entry_fee_per_gameweek;
			gameConfig.value.fixedPrizeAmount = val.fixed_prize_pool_amount;
			gameConfig.value.maxRolloverAmount = val.maximum_amount || 0;
			gameConfig.value.rolloverAmount = val.rollover_amount || 0;
			gameConfig.value.prizePoolType = "Fixed Amount";

			// Store original values for change detection
			originalGameConfig.value.entryFee = val.entry_fee_per_gameweek;
			originalGameConfig.value.fixedPrizeAmount = val.fixed_prize_pool_amount;
			originalGameConfig.value.maxRolloverAmount = val.maximum_amount || 0;
			originalGameConfig.value.rolloverAmount = val.rollover_amount || 0;
			originalGameConfig.value.prizePoolType = "Fixed Amount";
		}

		if (legal_Documents) {
			legalDocuments.value = legal_Documents
		}
	} catch (error) {
		console.log("🚀 ~ GetDetails ~ error:", error);
	} finally {
		isLoading.value = false;
	}
}

const fetchPrizePoolData = async () => {
	try {
		const response = await fetch('/admin/analytics', {
			method: 'GET',
		});

		if (!response.ok) {
			console.error('Failed to fetch prize pool data');
			return;
		}

		const data = await response.json();
		if (data && data.weekly_prize_info && data.weekly_prize_info.prize_info) {
			const prizeInfo = data.weekly_prize_info.prize_info;
			console.log("🚀 ~ fetchPrizePoolData ~ prizeInfo:", prizeInfo)
			prizePoolData.value = {
				total_prize_amount: prizeInfo.total_prize_amount || 0,
				base_prize_amount: prizeInfo.base_prize_amount || 0,
				rollover_amount: prizeInfo.rollover_amount || 0,
				maximum_prize_pool_withrollover: prizeInfo.maximum_prize_pool_withrollover || 0,
				future_rollover_amount: prizeInfo.future_rollover_amount || 0,
			};

			// Update edit values to match current data
			prizePoolEdit.value.base_prize_amount = prizeInfo.base_prize_amount || 0;
			prizePoolEdit.value.future_rollover_amount = prizeInfo.future_rollover_amount || 0;
			prizePoolEdit.value.maximum_prize_pool_withrollover = prizeInfo.maximum_prize_pool_withrollover || 0;

			// Store original values
			originalPrizePoolData.value.base_prize_amount = prizeInfo.base_prize_amount || 0;
			originalPrizePoolData.value.future_rollover_amount = prizeInfo.future_rollover_amount || 0;
			originalPrizePoolData.value.maximum_prize_pool_withrollover = prizeInfo.maximum_prize_pool_withrollover || 0;
		}
	} catch (error) {
		console.error('Error fetching prize pool data:', error);
	}
};

const togglePrizePoolEditMode = () => {
	if (!isEditingPrizePool.value) {
		// Store original values when entering edit mode
		originalPrizePoolData.value.base_prize_amount = prizePoolEdit.value.base_prize_amount;
		originalPrizePoolData.value.future_rollover_amount = prizePoolEdit.value.future_rollover_amount;
		originalPrizePoolData.value.maximum_prize_pool_withrollover = prizePoolEdit.value.maximum_prize_pool_withrollover;
		originalGameConfig.value.entryFee = gameConfig.value.entryFee;
	}
	isEditingPrizePool.value = !isEditingPrizePool.value;
};

const cancelPrizePoolEditMode = () => {
	// Revert values back to original
	prizePoolEdit.value.base_prize_amount = originalPrizePoolData.value.base_prize_amount;
	prizePoolEdit.value.future_rollover_amount = originalPrizePoolData.value.future_rollover_amount;
	prizePoolEdit.value.maximum_prize_pool_withrollover = originalPrizePoolData.value.maximum_prize_pool_withrollover;
	gameConfig.value.entryFee = originalGameConfig.value.entryFee;
	prizePoolValidationError.value = '';
	isEditingPrizePool.value = false;
};

const savePrizePoolSettings = async () => {
	if (!isEditingPrizePool.value) {
		return;
	}

	prizePoolValidationError.value = '';

	// Validate entry fee
	if (gameConfig.value.entryFee < 0) {
		prizePoolValidationError.value = 'Entry fee must be a non-negative number';
		return;
	}

	// Validate base prize amount
	const basePrize = prizePoolEdit.value.base_prize_amount;
	if (basePrize != null) {
		const basePrizeNum = Number(basePrize);
		if (basePrizeNum < 100 || basePrizeNum > 99999) {
			prizePoolValidationError.value = 'Base Prize Pool must be between 100 and 99999';
			return;
		}
	}

	// Validate maximum amount
	const maxPrize = prizePoolEdit.value.maximum_prize_pool_withrollover;
	if (maxPrize != null) {
		const maxPrizeNum = Number(maxPrize);
		if (maxPrizeNum < 100 || maxPrizeNum > 99999) {
			prizePoolValidationError.value = 'Maximum Prize Pool must be between 100 and 99999';
			return;
		}
	}

	// Validate that maximum is greater than base
	if (maxPrize != null && basePrize != null && Number(maxPrize) <= Number(basePrize)) {
		prizePoolValidationError.value = 'Maximum Prize Pool must be greater than Base Prize Pool';
		return;
	}

	// Validate future rollover amount
	if (prizePoolEdit.value.future_rollover_amount < 0) {
		prizePoolValidationError.value = 'Future Rollover Amount must be non-negative';
		return;
	}

	try {
		const response = await fetch('/platform_setting', {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
			body: JSON.stringify({
				entry_fee_per_gameweek: gameConfig.value.entryFee,
				fixed_prize_pool_amount: prizePoolEdit.value.base_prize_amount,
				rollover_amount: prizePoolEdit.value.future_rollover_amount,
				maximum_amount: prizePoolEdit.value.maximum_prize_pool_withrollover,
			}),
		});

		if (!response.ok) {
			const err = await response.json();
			console.error("❌ Failed to update prize pool settings:", err);
			if (err.errors && Array.isArray(err.errors)) {
				prizePoolValidationError.value = err.errors.join(', ');
			} else if (err.error) {
				prizePoolValidationError.value = err.error;
			} else {
				prizePoolValidationError.value = 'Failed to update prize pool settings. Please check your input.';
			}
			return;
		}

		const val = await response.json();
		console.log("✅ Prize pool settings updated successfully:", val);

		// Update original values after successful save
		originalPrizePoolData.value.base_prize_amount = prizePoolEdit.value.base_prize_amount;
		originalPrizePoolData.value.future_rollover_amount = prizePoolEdit.value.future_rollover_amount;
		originalPrizePoolData.value.maximum_prize_pool_withrollover = prizePoolEdit.value.maximum_prize_pool_withrollover;
		originalGameConfig.value.entryFee = gameConfig.value.entryFee;
		// Refresh prize pool data
		await fetchPrizePoolData();

		// Also update gameConfig to keep them in sync
		gameConfig.value.fixedPrizeAmount = prizePoolEdit.value.base_prize_amount;
		gameConfig.value.rolloverAmount = prizePoolEdit.value.future_rollover_amount;
		gameConfig.value.maxRolloverAmount = prizePoolEdit.value.maximum_prize_pool_withrollover;
		originalGameConfig.value.fixedPrizeAmount = prizePoolEdit.value.base_prize_amount;
		originalGameConfig.value.rolloverAmount = prizePoolEdit.value.future_rollover_amount;
		originalGameConfig.value.maxRolloverAmount = prizePoolEdit.value.maximum_prize_pool_withrollover;

		prizePoolValidationError.value = '';
		isEditingPrizePool.value = false;
		showSettingsSuccess.value = true;
	} catch (error) {
		console.error("⚠️ Error updating prize pool settings:", error);
		prizePoolValidationError.value = 'Failed to update prize pool settings. Please try again.';
	}
};

const formatNumber = (num) => {
	if (num == null || num === undefined) return '0.00';
	const numValue = Number(num);
	if (isNaN(numValue)) return '0.00';
	return numValue.toFixed(2);
};

const closeSettingsSuccess = () => {
	showSettingsSuccess.value = false;
};

const openModal = (doc = null) => {
	showModal.value = true
	showSuccess.value = false
	fileError.value = ''
	isReplaceMode.value = doc.document_type ? true : false

	if (doc) {
		documentType.value = doc.document_type
		selectedDocId.value = doc.id
	} else {
		documentType.value = ''
		selectedDocId.value = null
	}
	selectedFile.value = null
	// Reset file input
	if (fileInput.value) {
		fileInput.value.value = ''
	}
}


const closeModal = () => {
	showModal.value = false
	showSuccess.value = false;
	fileError.value = '';
	documentType.value = ''
	selectedFile.value = null;
	// Reset file input
	if (fileInput.value) {
		fileInput.value.value = ''
	}
	GetDetails();
}

const closeDocumentSuccess = () => {
	showDocumentSuccess.value = false;
}

const handleFileChange = (event) => {
	const file = event.target.files[0]
	fileError.value = ''

	if (file) {
		// Get file extension
		const fileName = file.name.toLowerCase()
		const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1)

		// Allowed file types: PDF
		const allowedExtensions = 'pdf'
		const allowedMimeTypes = 'application/pdf'

		// Check if file extension is allowed
		if (!allowedExtensions.includes(fileExtension)) {
			fileError.value = 'Invalid file type. Only PDF files are allowed.'
			selectedFile.value = null
			// Clear the file input
			if (event.target) {
				event.target.value = ''
			}
			return
		}

		// Check MIME type as additional validation
		if (!allowedMimeTypes.includes(file.type) && file.type !== '') {
			// Some browsers may not set MIME type correctly, so we allow if extension is valid
			// But if MIME type is set and doesn't match, show warning
			if (file.type && !file.type.includes('pdf')) {
				fileError.value = 'Invalid file type. Only PDF files are allowed.'
				selectedFile.value = null
				if (event.target) {
					event.target.value = ''
				}
				return
			}
		}

		selectedFile.value = file
	}
}

const uploadDocument = async () => {
	if (!selectedFile.value || !documentType.value) return

	// Validate file type again before upload
	const fileName = selectedFile.value.name.toLowerCase()
	const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1)
	const allowedExtensions = ['pdf', 'doc', 'docx', 'txt']

	if (!allowedExtensions.includes(fileExtension)) {
		fileError.value = 'Invalid file type. Only PDF, DOC, DOCX, and TXT files are allowed.'
		return
	}

	loading.value = true
	fileError.value = ''

	const formData = new FormData()
	formData.append('file', selectedFile.value)
	formData.append('document_type', documentType.value)

	try {
		let response
		if (isReplaceMode.value && selectedDocId.value) {
			// Replace existing document
			response = await fetch(`/legal_documents/${selectedDocId.value}`, {
				method: 'PATCH',
				body: formData,
			})
		} else {
			// Upload new document
			response = await fetch('/legal_documents', {
				method: 'POST',
				body: formData,
			})
		}

		const result = await response.json()
		console.log('📄 Upload/Replace result:', result)

		if (response.ok) {
			documentActionType.value = isReplaceMode.value ? 'replaced' : 'uploaded';
			closeModal();
			showDocumentSuccess.value = true;
		} else {
			alert(result.error || 'Failed to upload document.')
		}
	} catch (err) {
		console.error('❌ Upload error:', err)
		alert('Failed to upload document.')
	} finally {
		loading.value = false
	}
}

// const downloadDocument = (id) => {
// 	window.open(`/legal_documents/${id}/download`, '_blank');
// };

const fetchWeeklyWinners = async () => {
	try {
		const res = await fetch('/admin/payout/all_weekly_winners', { method: 'GET' });
		console.log("🚀 ~ fetchWeeklyWinners ~ res:", res);
		if (!res.ok) {
			weeklyWinnersData.value = []
			return
		}
		const payload = await res.json();

		// Transform the API response to match table structure
		const flatData = [];
		if (payload.weekly_winners && Array.isArray(payload.weekly_winners)) {
			payload.weekly_winners.forEach((week) => {
				if (week.winners && Array.isArray(week.winners)) {
					week.winners.forEach((winner) => {
						flatData.push({
							user_id: winner.user_id,
							username: winner.username || 'Unknown',
							gameweek: week.week_number || 0,
							game_week_id: week.game_week_id,
							points_scored: winner.user_points || 0,
							prize_amount: winner.prize_money || 0,
							claim_status: winner.payout_done ? 'Claimed' : 'Pending',
							payout_status: winner.payout_status || (winner.payout_done ? 'pending' : 'pending'),
							payout_type: 'Weekly',
							payout_period: (week.game_week_id ? week.game_week_id.toString() : ''),
							payout_done: winner.payout_done,
						});
					});
				}
			});
		}
		weeklyWinnersData.value = flatData;
	} catch (e) {
		console.log('Failed to load weekly winners:', e);
		weeklyWinnersData.value = [];
	}
}

const fetchMonthlyWinners = async () => {
	try {
		const res = await fetch('/admin/payout/all_monthly_winners', { method: 'GET' });
		if (!res.ok) {
			monthlyWinnersData.value = []
			return
		}
		const payload = await res.json();

		// Transform the API response to match table structure
		const flatData = [];
		if (payload.monthly_winners && Array.isArray(payload.monthly_winners)) {
			payload.monthly_winners.forEach((monthData) => {
				if (monthData.winners && Array.isArray(monthData.winners)) {
					monthData.winners.forEach((winner) => {
						flatData.push({
							user_id: winner.user_id,
							username: winner.username || 'Unknown',
							month: monthData.month_name || monthData.month || 'Unknown',
							month_period: monthData.month || '',
							points_scored: winner.user_points || 0,
							prize_amount: winner.prize_money || 0,
							claim_status: winner.payout_done ? 'Claimed' : 'Pending',
							payout_status: winner.payout_status || (winner.payout_done ? 'pending' : 'pending'),
							payout_type: 'Monthly',
							payout_period: monthData.month || '',
							payout_done: winner.payout_done,
						});
					});
				}
			});
		}
		monthlyWinnersData.value = flatData;
	} catch (e) {
		console.log('Failed to load monthly winners:', e);
		monthlyWinnersData.value = [];
	}
}

const approvePayout = async (row) => {
	try {
		console.log('Approve payout requested for:', row);

		if (!row.user_id || !row.payout_type || !row.payout_period) {
			alert('Missing payout information. Cannot approve payout.');
			return;
		}

		const userId = encodeURIComponent(row.user_id);
		const payoutType = encodeURIComponent(row.payout_type);
		const payoutPeriod = encodeURIComponent(row.payout_period);

		const res = await fetch(`/admin/payout/approve_payout/${userId}/${payoutType}/${payoutPeriod}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
		});

		if (res.ok) {
			const result = await res.json();
			console.log('Payout approved:', result);

			// Refresh the data to get updated status
			winnersLoading.value = true;
			try {
				if (paymentSubTab.value === 'weekly') {
					await fetchWeeklyWinners();
				} else {
					await fetchMonthlyWinners();
				}
			} finally {
				winnersLoading.value = false;
			}
		} else {
			const error = await res.json();
			console.error('Failed to approve payout:', error);
			alert(error.error || 'Failed to approve payout. Please try again.');
		}
	} catch (e) {
		console.log('Approve payout failed:', e);
		alert('Failed to approve payout. Please try again.');
	}
}

const markCompleted = async (row) => {
	try {
		console.log('Mark payout completed for:', row);

		if (!row.user_id || !row.payout_type || !row.payout_period) {
			alert('Missing payout information. Cannot complete payout.');
			return;
		}

		const userId = encodeURIComponent(row.user_id);
		const payoutType = encodeURIComponent(row.payout_type);
		const payoutPeriod = encodeURIComponent(row.payout_period);

		const res = await fetch(`/admin/payout/complete_payout/${userId}/${payoutType}/${payoutPeriod}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
		});

		if (res.ok) {
			const result = await res.json();
			console.log('Payout completed:', result);

			// Refresh the data to get updated status
			winnersLoading.value = true;
			try {
				if (paymentSubTab.value === 'weekly') {
					await fetchWeeklyWinners();
				} else {
					await fetchMonthlyWinners();
				}
			} finally {
				winnersLoading.value = false;
			}
		} else {
			const error = await res.json();
			console.error('Failed to complete payout:', error);
			alert(error.error || 'Failed to complete payout. Please try again.');
		}
	} catch (e) {
		console.log('Mark completed failed:', e);
		alert('Failed to complete payout. Please try again.');
	}
}

const viewPayoutDetails = async (row) => {
	try {
		if (!row.user_id || !row.payout_type || !row.payout_period) {
			alert('Missing payout information. Cannot fetch payout details.');
			return;
		}

		payoutDetailsLoading.value = true;
		showPayoutDetailsModal.value = true;

		const userId = encodeURIComponent(row.user_id);
		const payoutType = encodeURIComponent(row.payout_type);
		const payoutPeriod = encodeURIComponent(row.payout_period);

		const res = await fetch(`/admin/payout/payout_details/${userId}/${payoutType}/${payoutPeriod}`, {
			method: 'GET',
		});

		if (res.ok) {
			const details = await res.json();
			payoutDetails.value = details;
		} else {
			const error = await res.json();
			console.error('Failed to fetch payout details:', error);
			payoutDetails.value = null;
			alert(error.error || 'Failed to fetch payout details. Please try again.');
		}
	} catch (e) {
		console.log('View payout details failed:', e);
		payoutDetails.value = null;
		alert('Failed to fetch payout details. Please try again.');
	} finally {
		payoutDetailsLoading.value = false;
	}
}

const closePayoutDetailsModal = () => {
	showPayoutDetailsModal.value = false;
	payoutDetails.value = null;
}

const formatPayoutStatus = (status) => {
	if (!status) return 'Pending';
	const statusLower = status.toLowerCase();
	if (statusLower === 'completed') return 'Completed';
	if (statusLower === 'approved') return 'Approved';
	if (statusLower === 'pending') return 'Pending';
	return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

const fetchActivityLogs = async () => {
	try {
		activityLogsLoading.value = true;
		const res = await fetch('/admin/admin_activity_logs', { method: 'GET' });
		console.log("🚀 ~ fetchActivityLogs ~ res:", res);
		if (!res.ok) {
			activityLogsData.value = [];
			return;
		}
		const payload = await res.json();
		activityLogsData.value = Array.isArray(payload.admin_activity_logs) ? payload.admin_activity_logs : [];
	} catch (e) {
		console.log('Failed to load activity logs:', e);
		activityLogsData.value = [];
	} finally {
		activityLogsLoading.value = false;
	}
}

const formatActionType = (actionType) => {
	if (!actionType) return 'Unknown';
	return actionType
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

const getActionTypeClass = (actionType) => {
	const classes = {
		'bg-blue-100 text-blue-800': ['postal_entry_create', 'postal_entry_update', 'legal_document_upload', 'legal_document_update'],
		'bg-green-100 text-green-800': ['user_activate'],
		'bg-red-100 text-red-800': ['user_suspend'],
		'bg-purple-100 text-purple-800': ['entry_fee_update', 'prize_fund_update', 'rollover_logic_update'],
	};

	for (const [classNames, types] of Object.entries(classes)) {
		if (types.includes(actionType)) {
			return classNames;
		}
	}
	return 'bg-gray-100 text-gray-800';
}

const formatDateTime = (dateTime) => {
	if (!dateTime) return 'N/A';
	try {
		const date = new Date(dateTime);
		return date.toLocaleString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch (e) {
		return dateTime;
	}
}

const formatMetadataKey = (key) => {
	return key
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

const formatMetadataValue = (value) => {
	if (value === null || value === undefined) return 'N/A';
	if (typeof value === 'object') {
		if (value.amount && value.direction && value.percentage) {
			return `${value.amount} (${value.direction}, ${value.percentage})`;
		}
		return JSON.stringify(value);
	}
	return String(value);
}

const formatChangeValue = (change) => {
	if (!change || typeof change !== 'object') return 'N/A';
	if (change.amount && change.direction && change.percentage) {
		return `${change.amount} (${change.direction}, ${change.percentage})`;
	}
	return JSON.stringify(change);
}

</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
	transform: translateY(-5px);
}

/* Modal transitions */
.modal-enter-active {
	transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active>div {
	animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active {
	transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active>div {
	animation: modalSlideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}

@keyframes modalSlideIn {
	from {
		transform: scale(0.9) translateY(-10px);
		opacity: 0;
	}

	to {
		transform: scale(1) translateY(0);
		opacity: 1;
	}
}

@keyframes modalSlideOut {
	from {
		transform: scale(1) translateY(0);
		opacity: 1;
	}

	to {
		transform: scale(0.9) translateY(-10px);
		opacity: 0;
	}
}
</style>
